import React from 'react'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
// @src imports
import { NavLink, ProfileAvatar } from 'src/chrome'
import Transition from 'src/chrome/Transition/Transition'
import { BottomNav, defaultItems } from 'src/chrome/BottomNav/BottomNav'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'
import { FlexColumn } from 'src/styled'
import { OrdersRoute } from 'src/orders/routes/OrdersRoute'
import { CatalogRoute } from 'src/catalog/routes/CatalogRoute'
import { CampaignsRoute } from 'src/campaigns/routes/CampaignsRoute'
import { RelationshipManagerRoute } from 'src/contacts/routes/RelationshipManagerRoute'
import { PricingRoute } from 'src/marketing/plans/routes/PricingRoute'
import { AccountRoute } from 'src/dashboard/routes/AccountRoute'
import { GiftStoreRoute } from 'src/gift_store/routes/GiftStoreRoute'
import {
  useAccount,
  useActions,
  useRoute,
  useSelector,
  useState,
} from 'src/hooks'
// relative imports

import theme from 'src/chrome/styles/Chrome.module.scss'
import { ConfirmDialog, Text, VStack } from '@sendoutcards/quantum-design-ui'
import { AddOrderGiftRoute } from 'src/orders/routes/AddOrderGiftRoute'
import { Icon as DSIcon } from 'src/design_system/atoms/icons/components/Icon'

const linkTitle: React.CSSProperties = {
  position: 'relative',
  top: 27,
  fontSize: 11,
  fontWeight: 600,
  color: '#424448',
}

const SIDEBAR_BOTTOM_NAV_BREAKPOINT = 900

const SideBar: React.FC = () => {
  const account = useAccount()

  const { height, width } = useSelector(state => state.window)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [showConfirmVisible, setShowConfirmVisible] = useState(false)
  const [buttonClicked, setButtonClicked] = useState('')
  const actions = useActions()
  const route = useRoute()
  const { order } = useSelector(state => state.orders)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const performNavigation = (buttonId: string) => {
    switch (buttonId) {
      case 'goto_catalog_btn':
        actions.openCatalog()
        break
      case 'goto_dashboard_btn':
        actions.openAccount()
        break
      case 'goto_pricing_btn':
        actions.openPricing()
        break
      case 'goto_campaigns_btn':
        actions.openCampaigns()
        break
      case 'goto_contacts_btn':
        actions.openRelationshipManager()
        break
      case 'goto_gift_store_btn':
        route.subroute.path === OrdersRoute.path && order?.id
          ? actions.openOrder(order.id, AddOrderGiftRoute())
          : actions.openGiftStore()
        break
      case 'goto_account_btn':
        actions.openPaymentSettings()
        break
      case 'goto_notifications_btn':
        actions.openNotifications()
        break
      case 'goto_payment_settings_btn':
        actions.openPaymentSettings()
        break
      case 'goto_card_history_btn':
        actions.openCardHistory()
        break
      case 'goto_drafted_orders_btn':
        actions.openDraftedCards()
        break
      case 'goto_drafted_cards_btn':
        actions.openDraftedCards()
        break
      case 'open_cookies_preferences':
        window.Osano.cm.showDrawer('osano-cm-dom-info-dialog-open')
        break
      case 'logout_btn':
        actions.logout()
        break
    }
  }

  const clickNavButton = (buttonId: string) => {
    const shouldShowDialog: boolean = route.subroute.path === OrdersRoute.path

    if (shouldShowDialog) {
      setButtonClicked(buttonId)
      setShowConfirmVisible(true)
      return
    }
    performNavigation(buttonId)
  }

  return (
    <nav className={theme.sideBar}>
      {width > SIDEBAR_BOTTOM_NAV_BREAKPOINT ? (
        height > 880 ? (
          <div
            className={theme.fullHeight}
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <NavLink
              id="goto_dashboard_btn"
              route={AccountRoute}
              activeClassName={theme.active}
              onClick={() => clickNavButton('goto_dashboard_btn')}
            >
              <DSIcon
                name="house"
                size={32}
                color="#424448"
                strokeWidth={1.5}
              />
              <Text
                alignment="center"
                type="footnote"
                content="Home"
                style={linkTitle}
              />
            </NavLink>
            <NavLink
              id="goto_catalog_btn"
              route={CatalogRoute}
              activeClassName={theme.active}
              onClick={() => clickNavButton('goto_catalog_btn')}
            >
              <DSIcon
                name="nav_catalog"
                size={32}
                color="#424448"
                strokeWidth={1.5}
              />
              <Text
                alignment="center"
                type="footnote"
                content="Catalog"
                style={linkTitle}
              />
            </NavLink>
            <NavLink
              id={'goto_campaigns_btn'}
              route={CampaignsRoute}
              activeClassName={theme.active}
              onClick={() => clickNavButton('goto_campaigns_btn')}
            >
              <DSIcon
                name="nav_campaign"
                size={32}
                color="#424448"
                strokeWidth={1.5}
              />
              <Text
                alignment="center"
                type="footnote"
                content="Campaigns"
                style={linkTitle}
              />
            </NavLink>
            <NavLink
              id={'goto_contacts_btn'}
              route={RelationshipManagerRoute}
              activeClassName={theme.active}
              onClick={() => clickNavButton('goto_contacts_btn')}
            >
              <DSIcon
                name="nav_contacts"
                size={32}
                color="#424448"
                strokeWidth={1.5}
              />
              <Text
                alignment="center"
                type="footnote"
                content="Contacts"
                style={linkTitle}
              />
            </NavLink>
            <NavLink
              id={'goto_gift_store_btn'}
              route={GiftStoreRoute}
              activeClassName={theme.active}
              onClick={() => performNavigation('goto_gift_store_btn')}
            >
              <DSIcon
                name="nav_gifts"
                size={32}
                color="#424448"
                strokeWidth={1.5}
              />
              <Text
                alignment="center"
                type="footnote"
                content="Gifts"
                style={linkTitle}
              />
            </NavLink>
            <NavLink
              id={'goto_pricing_btn'}
              route={PricingRoute}
              activeClassName={theme.active}
              onClick={() => performNavigation('goto_pricing_btn')}
            >
              <DSIcon
                name="nav_pricing"
                size={32}
                color="#424448"
                strokeWidth={1.5}
              />
              <Text
                alignment="center"
                type="footnote"
                content="Pricing"
                style={linkTitle}
              />
            </NavLink>
            <FlexColumn
              style={{
                justifyContent: 'center',
                marginTop: 40,
                marginBottom: 'auto',
              }}
            >
              <button
                id="avatar_btn"
                aria-controls="drop-down-menu"
                aria-haspopup="true"
                onClick={handleClick}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                }}
              >
                <ProfileAvatar
                  account={account}
                  size={32}
                  style={{
                    zIndex: 300,
                    fontSize: '1.1rem',
                    color: '#fff',
                    letterSpacing: 1,
                  }}
                  isEditable={false}
                />
                <Text
                  type="footnote"
                  content="Account"
                  style={{ ...linkTitle, textAlign: 'center', top: 4 }}
                />
              </button>
              <Menu
                id="drop-down-menu"
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                keepMounted={false}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem
                  id="goto_dashboard_btn"
                  onClick={() => clickNavButton('goto_dashboard_btn')}
                  style={{ fontSize: 'initial', padding: '4px 16px' }}
                >
                  <Text type="body">{'Home'}</Text>
                </MenuItem>
                <MenuItem
                  id="goto_account_btn"
                  onClick={() => clickNavButton('goto_account_btn')}
                  style={{ fontSize: 'initial', padding: '4px 16px' }}
                >
                  <Text type="body">{'My Account'}</Text>
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    window.open(
                      'https://www.promptings.com/help',
                      '_blank',
                      'noreferrer',
                    )
                  }
                  style={{ fontSize: 'initial', padding: '4px 16px' }}
                >
                  <Text type="body">{'Help Center'}</Text>
                </MenuItem>
                <MenuItem
                  id="logout_btn"
                  onClick={() => clickNavButton('logout_btn')}
                  style={{ fontSize: 'initial', padding: '4px 16px' }}
                >
                  <Text type="body">{'Logout'}</Text>
                </MenuItem>
              </Menu>
            </FlexColumn>
            <NavLink
              id={'open_cookies_preferences'}
              onClick={() => performNavigation('open_cookies_preferences')}
            >
              <DSIcon
                name="nav_cookie"
                size={32}
                color="#424448"
                strokeWidth={1.5}
              />
              <VStack gap={'x0'}>
                <Text
                  type="footnote"
                  content="Storage"
                  style={{ ...linkTitle, textAlign: 'center', top: 32 }}
                />
                <Text
                  type="footnote"
                  content="Preferences"
                  style={{ ...linkTitle, textAlign: 'center', top: 26 }}
                />
              </VStack>
            </NavLink>
          </div>
        ) : (
          <BottomNav items={defaultItems} isSidebarEnabled={true} />
        )
      ) : (
        <div className={theme.mobile}>
          <BottomNav items={defaultItems} />
        </div>
      )}
      {showConfirmVisible && (
        <ConfirmDialog
          title="You are leaving the order checkout page."
          description="Your card is saved in your Account under Drafted Orders."
          primaryAction="decline"
          isOpen={showConfirmVisible}
          accept={{
            title: 'Continue and navigate away.',
            onClick: () => {
              performNavigation(buttonClicked)
              actions.clearOrder()
              setShowConfirmVisible(false)
            },
          }}
          decline={{
            title: 'Cancel and stay on this page.',
            onClick: () => setShowConfirmVisible(false),
          }}
          onClose={() => {
            setShowConfirmVisible(false)
          }}
        />
      )}
    </nav>
  )
}

export default suspenseBoundary({
  component: SideBar,
  unresolved: <Transition message={'Loading sidebar...'} />,
  failure: error => `Failed loading sidebar: ${error}`,
})
